import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { SignIn } from '../components/sign-in';
import type { AdminConfig } from '../config';
import { createApi, type Api } from './api';
import { freshSession, loadSession, signOut, storeSession, type AdminSession } from './cognito';

interface Me {
  sub: string;
  email: string | null;
  groups: string[];
}

interface Session {
  config: AdminConfig;
  me: Me | null;
  api: Api;
  /** access token valido (per le impostazioni dell'account su Cognito) */
  accessToken: () => Promise<string>;
  /** può modificare gli eventi (editor o admin) */
  canEdit: boolean;
  /** amministratore: impostazioni del sito, banner, sorgenti */
  isAdmin: boolean;
  logout: () => Promise<void>;
}

const SessionContext = createContext<Session | null>(null);

export function useSession(): Session {
  const s = useContext(SessionContext);
  if (!s) throw new Error('useSession fuori da SessionProvider');
  return s;
}

/** Accesso con il form del pannello (lib/cognito.ts) e profilo; senza sessione mostra il form. */
export function SessionProvider({
  config,
  children,
}: {
  config: AdminConfig;
  children: ReactNode;
}) {
  const [session, setSession] = useState<AdminSession | null>(() => loadSession());
  const [me, setMe] = useState<Me | null>(null);
  const [error, setError] = useState<string | null>(null);
  const current = useRef(session);
  current.current = session;

  /** Sessione con i token validi: rinnova l'id token se sta per scadere. */
  const valid = async () => {
    const s = current.current;
    if (!s) return null;
    const next = await freshSession(config, s);
    if (next !== s) setSession(next);
    return next;
  };

  const api = useMemo(
    () =>
      createApi(
        config.apiUrl,
        async () => (await valid())?.idToken,
        () => {
          storeSession(null);
          setSession(null);
        },
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [config.apiUrl, session === null],
  );

  useEffect(() => {
    if (!session) return setMe(null);
    api<Me>('/me').then(setMe, (e) => setError(String(e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session === null, api]);

  if (!session)
    return (
      <SignIn
        config={config}
        onSignedIn={(s) => {
          setError(null);
          setSession(s);
        }}
      />
    );
  const value: Session = {
    config,
    me,
    api,
    accessToken: async () => {
      const s = await valid();
      if (!s) throw new Error('Sessione scaduta: entra di nuovo.');
      return s.accessToken;
    },
    canEdit: Boolean(me?.groups.some((g) => g === 'admin' || g === 'editor')),
    isAdmin: Boolean(me?.groups.includes('admin')),
    logout: async () => {
      await signOut(config, current.current);
      setSession(null);
    },
  };
  return (
    <SessionContext.Provider value={value}>
      {error && (
        <p role="alert" className="bg-danger-soft px-4 py-2 text-sm">
          Errore: {error}
        </p>
      )}
      {children}
    </SessionContext.Provider>
  );
}
